/**
 * AddDoctorModal — добавить врача в клинику админ-сайдом
 * (POST /admin/clients/:id/doctors). Роль всегда «Врач», занимает одно
 * место; лимит проверяется в StaffTab. Специализация обязательна.
 */

import {useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';

import {addDoctor} from '@services/clients';
import {keys} from '@queries/keys';
import {useSpecializationOptions} from '@hooks/useFilterRefs';
import {Field, TextInput} from '@components/FormKit';
import {cn} from '@lib/utils';
import {formatKgPhone, phoneDigits} from '@utils/phone';

import {ModalShell} from '../ModalShell';

type Props = {
  open: boolean;
  clientId: string;
  seatsLeft: number;
  seatsLimit: number;
  onClose: () => void;
  onSaved: () => void;
};

export const AddDoctorModal = ({
  open,
  clientId,
  seatsLeft,
  seatsLimit,
  onClose,
  onSaved,
}: Props) => {
  const queryClient = useQueryClient();
  const specializationOptions = useSpecializationOptions();
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [specialization, setSpecialization] = useState('');

  const [prevOpen, setPrevOpen] = useState(false);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setFullName('');
      setPhone('');
      setSpecialization('');
    }
  }

  const mutation = useMutation({
    mutationFn: addDoctor,
    onSuccess: () => {
      void queryClient.invalidateQueries({queryKey: keys.clients.all});
      onSaved();
    },
  });

  const valid =
    fullName.trim().length >= 2 && phoneDigits(phone).length === 9 && specialization !== '';

  const handleSubmit = () => mutation.mutate({clientId, fullName, phone, specialization});

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Добавить врача"
      subtitle={
        <>
          Займёт 1 место · останется <span className="font-mono">{Math.max(0, seatsLeft - 1)}</span>{' '}
          из <span className="font-mono">{seatsLimit}</span>
        </>
      }
      submitLabel="Добавить"
      onSubmit={handleSubmit}
      pending={mutation.isPending}
      submitDisabled={!valid}
      error={mutation.isError ? 'Не удалось добавить врача. Попробуйте ещё раз' : null}
    >
      <Field label="ФИО врача">
        <TextInput
          placeholder="Например, Осмонова Динара"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
        />
      </Field>
      <Field label="Телефон · логин">
        <TextInput
          mono
          placeholder="700 000 000"
          value={formatKgPhone(phoneDigits(phone))}
          onChange={(e) => setPhone(phoneDigits(e.target.value))}
        />
      </Field>
      <Field label="Специализация">
        <select
          value={specialization}
          onChange={(e) => setSpecialization(e.target.value)}
          className={cn(
            'h-11 w-full appearance-none rounded-[10px] border border-input bg-card px-[13px] text-sm outline-none transition',
            'focus:border-primary focus:ring-[3px] focus:ring-primary/12',
            specialization === '' && 'text-muted-foreground',
          )}
        >
          <option value="" disabled>
            Выберите специализацию
          </option>
          {specializationOptions.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </Field>
    </ModalShell>
  );
};
